import { configManagerCloudServicesApi as api } from '../configManagerCloudServicesApi';
import { withMutationNotifications } from 'app/shared/api/rtk-mutation-notifications';
import {
  AddRoleToMemberApiArg,
  AddRoleToMemberApiResponse,
  CancelSubscriptionForCurrentAccountApiArg,
  CancelSubscriptionForCurrentAccountApiResponse,
  CreateAccountApiArg,
  CreateAccountApiResponse,
  CreateOrganizationAccountApiArg,
  CreateOrganizationAccountApiResponse,
  CreateSubAccountApiArg,
  CreateSubAccountApiResponse,
  ListSubAccountsApiArg,
  ListSubAccountsApiResponse,
  SwitchCurrentAccountApiArg,
  SwitchCurrentAccountApiResponse,
  DeactivateCurrentAccountApiArg,
  DeactivateCurrentAccountApiResponse,
  DeleteAccountApiArg,
  DeleteAccountApiResponse,
  GetAccountByIdApiArg,
  GetAccountByIdApiResponse,
  GetCurrentAccountProfileApiArg,
  GetCurrentAccountProfileApiResponse,
  InviteMemberToAccountApiArg,
  InviteMemberToAccountApiResponse,
  ListAccountMembersApiArg,
  ListAccountMembersApiResponse,
  ListAllAccountsApiArg,
  ListAllAccountsApiResponse,
  ListSubscriptionsForAccountApiArg,
  ListSubscriptionsForAccountApiResponse,
  ReactivateCurrentAccountApiArg,
  ReactivateCurrentAccountApiResponse,
  RemoveMemberFromCurrentAccountApiArg,
  RemoveMemberFromCurrentAccountApiResponse,
  RemoveRoleFromMemberApiArg,
  RemoveRoleFromMemberApiResponse,
  SetCurrentAccountMemberRolesApiArg,
  SetCurrentAccountMemberRolesApiResponse,
  SetCurrentAccountMemberStatusApiArg,
  SetCurrentAccountMemberStatusApiResponse,
  SubscribeAccountToProductApiArg,
  SubscribeAccountToProductApiResponse,
  TransferAccountOwnershipApiArg,
  TransferAccountOwnershipApiResponse,
  UpdateAccountDetailsApiArg,
  UpdateAccountDetailsApiResponse,
  UpdateCurrentAccountDetailsApiArg,
  UpdateCurrentAccountDetailsApiResponse,
} from './types';

const injectedRtkApi = api.injectEndpoints({
  endpoints: build => ({
    createAccount: build.mutation<CreateAccountApiResponse, CreateAccountApiArg>({
      query: () => ({ url: `/config-manager-cloud-services/api/v1/account-profiles/new`, method: 'POST' }),
      invalidatesTags: ['ACCOUNT_LIST'],
    }),
    createOrganizationAccount: build.mutation<CreateOrganizationAccountApiResponse, CreateOrganizationAccountApiArg>({
      query: queryArg => ({
        url: `/config-manager-cloud-services/api/v1/account-profiles/organizations`,
        method: 'POST',
        params: { name: queryArg.name },
      }),
      invalidatesTags: ['ACCOUNT_LIST', 'USER_PROFILE'],
    }),
    switchCurrentAccount: build.mutation<SwitchCurrentAccountApiResponse, SwitchCurrentAccountApiArg>({
      query: queryArg => ({
        url: `/config-manager-cloud-services/api/v1/account-profiles/${queryArg.accountId}/switch`,
        method: 'POST',
      }),
      invalidatesTags: [
        'USER_PROFILE',
        'ACCOUNT_PROFILE',
        'BILLING_PROFILE',
        'SUBSCRIPTIONS',
        'MEMBER_LIST',
        'INVITATION_LIST',
        'SUBACCOUNT_LIST',
      ],
    }),
    listSubAccounts: build.query<ListSubAccountsApiResponse, ListSubAccountsApiArg>({
      query: () => ({ url: `/config-manager-cloud-services/api/v1/account-profiles/current/sub-accounts` }),
      providesTags: ['SUBACCOUNT_LIST'],
    }),
    createSubAccount: build.mutation<CreateSubAccountApiResponse, CreateSubAccountApiArg>({
      query: queryArg => ({
        url: `/config-manager-cloud-services/api/v1/account-profiles/current/sub-accounts`,
        method: 'POST',
        params: { name: queryArg.name },
      }),
      invalidatesTags: ['SUBACCOUNT_LIST', 'USER_PROFILE', 'ACCOUNT_LIST'],
    }),
    getCurrentAccountProfile: build.query<GetCurrentAccountProfileApiResponse, GetCurrentAccountProfileApiArg>({
      query: () => ({ url: `/config-manager-cloud-services/api/v1/account-profiles/current` }),
      providesTags: ['ACCOUNT_PROFILE'],
    }),
    updateCurrentAccountDetails: build.mutation<
      UpdateCurrentAccountDetailsApiResponse,
      UpdateCurrentAccountDetailsApiArg
    >({
      query: queryArg => ({
        url: `/config-manager-cloud-services/api/v1/account-profiles/current`,
        method: 'PUT',
        body: queryArg.updates,
      }),
      invalidatesTags: ['ACCOUNT_PROFILE', 'USER_PROFILE'],
    }),
    deactivateCurrentAccount: build.mutation<DeactivateCurrentAccountApiResponse, DeactivateCurrentAccountApiArg>({
      query: () => ({
        url: `/config-manager-cloud-services/api/v1/account-profiles/current/deactivate`,
        method: 'POST',
      }),
      invalidatesTags: ['ACCOUNT_PROFILE'],
    }),
    reactivateCurrentAccount: build.mutation<ReactivateCurrentAccountApiResponse, ReactivateCurrentAccountApiArg>({
      query: () => ({
        url: `/config-manager-cloud-services/api/v1/account-profiles/current/reactivate`,
        method: 'POST',
      }),
      invalidatesTags: ['ACCOUNT_PROFILE'],
    }),
    transferAccountOwnership: build.mutation<TransferAccountOwnershipApiResponse, TransferAccountOwnershipApiArg>({
      query: queryArg => ({
        url: `/config-manager-cloud-services/api/v1/account-profiles/current/transfer-ownership`,
        method: 'POST',
        params: {
          new_owner_email: queryArg.newOwnerEmail,
        },
      }),
      invalidatesTags: ['ACCOUNT_PROFILE'],
    }),
    getAccountById: build.query<GetAccountByIdApiResponse, GetAccountByIdApiArg>({
      query: queryArg => ({ url: `/config-manager-cloud-services/api/v1/account-profiles/${queryArg.accountId}` }),
      providesTags: ['ACCOUNT_PROFILE'],
    }),
    updateAccountDetails: build.mutation<UpdateAccountDetailsApiResponse, UpdateAccountDetailsApiArg>({
      query: queryArg => ({
        url: `/config-manager-cloud-services/api/v1/account-profiles/${queryArg.accountId}`,
        method: 'PUT',
        body: queryArg.updates,
      }),
      invalidatesTags: ['ACCOUNT_PROFILE', 'ACCOUNT_LIST', 'USER_PROFILE'],
    }),
    deleteAccount: build.mutation<DeleteAccountApiResponse, DeleteAccountApiArg>({
      query: queryArg => ({
        url: `/config-manager-cloud-services/api/v1/account-profiles/${queryArg.accountId}`,
        method: 'DELETE',
      }),
    }),
    listAllAccounts: build.query<ListAllAccountsApiResponse, ListAllAccountsApiArg>({
      query: () => ({ url: `/config-manager-cloud-services/api/v1/account-profiles/` }),
      providesTags: ['ACCOUNT_LIST'],
    }),
    removeMemberFromCurrentAccount: build.mutation<
      RemoveMemberFromCurrentAccountApiResponse,
      RemoveMemberFromCurrentAccountApiArg
    >({
      query: queryArg => ({
        url: `/config-manager-cloud-services/api/v1/account-profiles/current/members/${queryArg.userEmail}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['MEMBER_LIST', 'ACCOUNT_PROFILE'],
      onQueryStarted: withMutationNotifications({
        success: () => 'Member removed.',
        error: () => 'Could not remove the member.',
      }),
    }),
    inviteMemberToAccount: build.mutation<InviteMemberToAccountApiResponse, InviteMemberToAccountApiArg>({
      query: queryArg => ({
        url: `/config-manager-cloud-services/api/v1/account-profiles/current/inviteMember`,
        method: 'POST',
        params: {
          invitee_email: queryArg.inviteeEmail,
          role: queryArg.role,
        },
      }),
      invalidatesTags: ['MEMBER_LIST', 'ACCOUNT_PROFILE'],
    }),
    listAccountMembers: build.query<ListAccountMembersApiResponse, ListAccountMembersApiArg>({
      query: () => ({ url: `/config-manager-cloud-services/api/v1/account-profiles/current/members` }),
      providesTags: ['MEMBER_LIST'],
    }),
    setCurrentAccountMemberRoles: build.mutation<
      SetCurrentAccountMemberRolesApiResponse,
      SetCurrentAccountMemberRolesApiArg
    >({
      query: queryArg => ({
        url: `/config-manager-cloud-services/api/v1/account-profiles/current/members/${queryArg.userEmail}/roles`,
        method: 'PUT',
        body: queryArg.roles,
      }),
      invalidatesTags: ['MEMBER_LIST'],
      onQueryStarted: withMutationNotifications({
        success: () => 'Role updated.',
        error: () => 'Could not update the role.',
      }),
    }),
    setCurrentAccountMemberStatus: build.mutation<
      SetCurrentAccountMemberStatusApiResponse,
      SetCurrentAccountMemberStatusApiArg
    >({
      query: queryArg => ({
        url: `/config-manager-cloud-services/api/v1/account-profiles/current/members/${queryArg.userEmail}/status`,
        method: 'PUT',
        params: { new_status: queryArg.newStatus },
      }),
      invalidatesTags: ['MEMBER_LIST', 'ACCOUNT_PROFILE'],
      onQueryStarted: withMutationNotifications({
        success: () => 'Member status updated.',
        error: () => 'Could not update the member status.',
      }),
    }),
    subscribeAccountToProduct: build.mutation<SubscribeAccountToProductApiResponse, SubscribeAccountToProductApiArg>({
      query: queryArg => ({
        url: `/config-manager-cloud-services/api/v1/account-profiles/${queryArg.accountId}/subscriptions`,
        method: 'POST',
        body: queryArg.subscriptionCreate,
      }),
    }),
    listSubscriptionsForAccount: build.query<ListSubscriptionsForAccountApiResponse, ListSubscriptionsForAccountApiArg>(
      {
        query: queryArg => ({
          url: `/config-manager-cloud-services/api/v1/account-profiles/${queryArg.accountId}/subscriptions`,
        }),
      },
    ),
    cancelSubscriptionForCurrentAccount: build.mutation<
      CancelSubscriptionForCurrentAccountApiResponse,
      CancelSubscriptionForCurrentAccountApiArg
    >({
      query: queryArg => ({
        url: `/config-manager-cloud-services/api/v1/account-profiles/current/subscriptions/${queryArg.subscriptionId}`,
        method: 'DELETE',
      }),
    }),
    addRoleToMember: build.mutation<AddRoleToMemberApiResponse, AddRoleToMemberApiArg>({
      query: queryArg => ({
        url: `/config-manager-cloud-services/api/v1/account-profiles/${queryArg.accountId}/members/${queryArg.userEmail}/roles`,
        method: 'POST',
        params: {
          role: queryArg.role,
        },
      }),
    }),
    removeRoleFromMember: build.mutation<RemoveRoleFromMemberApiResponse, RemoveRoleFromMemberApiArg>({
      query: queryArg => ({
        url: `/config-manager-cloud-services/api/v1/account-profiles/${queryArg.accountId}/members/${queryArg.userEmail}/roles/${queryArg.role}`,
        method: 'DELETE',
      }),
    }),
  }),
  overrideExisting: false,
});
export { injectedRtkApi as accountProfileApi };

export const {
  useCreateAccountMutation,
  useCreateOrganizationAccountMutation,
  useListSubAccountsQuery,
  useCreateSubAccountMutation,
  useSwitchCurrentAccountMutation,
  useGetCurrentAccountProfileQuery,
  useUpdateCurrentAccountDetailsMutation,
  useDeactivateCurrentAccountMutation,
  useReactivateCurrentAccountMutation,
  useTransferAccountOwnershipMutation,
  useGetAccountByIdQuery,
  useUpdateAccountDetailsMutation,
  useDeleteAccountMutation,
  useListAllAccountsQuery,
  useRemoveMemberFromCurrentAccountMutation,
  useInviteMemberToAccountMutation,
  useListAccountMembersQuery,
  useSetCurrentAccountMemberRolesMutation,
  useSetCurrentAccountMemberStatusMutation,
  useSubscribeAccountToProductMutation,
  useListSubscriptionsForAccountQuery,
  useCancelSubscriptionForCurrentAccountMutation,
  useAddRoleToMemberMutation,
  useRemoveRoleFromMemberMutation,
} = injectedRtkApi;
